import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, 
  Bike, 
  Wrench, 
  Map, 
  HardHat, 
  Wallet, 
  Bell, 
  FileText, 
  BarChart3,
  LogOut,
  Menu,
  X,
  Settings2,
  ChevronDown,
  User,
  Camera,
  Heart,
  Users,
  Settings,
  Fuel,
  CreditCard,
  QrCode,
  Shield
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { LanguageSelector } from './LanguageSelector';
import { TourGuide } from './TourGuide';
import { SubscriptionGuard } from './SubscriptionGuard';
import { SupportButton } from './SupportButton';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moto-dashboard-4/artifacts/tifo19c3_grok_image_1769707994767.jpg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getNavItems = (t) => [
  { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
  { to: '/moto', icon: Bike, label: t('nav.myMoto') },
  { to: '/modifiche', icon: Settings2, label: t('nav.modifications') },
  { to: '/manutenzione', icon: Wrench, label: t('nav.maintenance') },
  { to: '/viaggi', icon: Map, label: t('nav.trips') },
  { to: '/equipaggiamento', icon: HardHat, label: t('nav.gear') },
  { to: '/spese', icon: Wallet, label: t('nav.expenses') },
  { to: '/stazioni-benzina', icon: Fuel, label: t('nav.fuelStations') },
  { to: '/promemoria', icon: Bell, label: t('nav.reminders') },
  { to: '/documenti', icon: FileText, label: t('nav.documents') },
  { to: '/statistiche', icon: BarChart3, label: t('nav.statistics') },
  { to: '/wishlist', icon: Heart, label: t('nav.wishlist') },
  { to: '/community', icon: Users, label: t('nav.community') },
  { to: '/abbonamento', icon: CreditCard, label: t('nav.subscription', 'Abbonamento') },
  { to: '/download', icon: QrCode, label: 'Scarica App' },
  { to: '/privacy', icon: Shield, label: 'Privacy' },
  { to: '/impostazioni', icon: Settings, label: t('nav.settings') },
];

export const Layout = ({ children }) => {
  const { user, logout, updateUser, setUserState, motoList, selectedMotoId, selectMoto, getSelectedMoto, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [motoSelectorOpen, setMotoSelectorOpen] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const fileInputRef = useRef(null);
  
  const navItems = getNavItems(t);

  // Show tour for new users
  useEffect(() => {
    if (user && user.tour_completed === false) {
      setShowTour(true);
    }
  }, [user]);

  const handleCompleteTour = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/user/complete-tour`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // Update local user state
      setUserState({ ...user, tour_completed: true });
    } catch (error) {
      console.error('Error completing tour:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const selectedMoto = getSelectedMoto();

  const getMotoImageUrl = (moto) => {
    if (moto?.foto_urls && moto.foto_urls.length > 0) {
      const url = moto.foto_urls[0];
      return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
    }
    return null;
  };

  const getAvatarUrl = () => {
    if (user?.avatar_url) {
      return user.avatar_url.startsWith('/api') ? `${BACKEND_URL}${user.avatar_url}` : user.avatar_url;
    }
    return null;
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setUploadingAvatar(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const uploadResponse = await api.post('/upload', {
          filename: file.name,
          content_type: file.type,
          data: base64
        });
        
        await updateUser({ avatar_url: uploadResponse.data.url });
        toast.success('Avatar aggiornato');
        setAvatarDialogOpen(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="app-layout">
      {/* Desktop Sidebar */}
      <aside className="sidebar hidden md:flex">
        <div className="sidebar-header">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="relative w-12 h-12 rounded overflow-hidden">
              <img 
                src={LOGO_URL} 
                alt="Vault Ride" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="font-['Teko'] text-2xl text-[#FF5F1F] uppercase tracking-tight leading-none">
                Vault Ride
              </h1>
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">
                Garage Manager
              </p>
            </div>
          </Link>
        </div>

        {/* Moto Selector */}
        {motoList.length > 0 && (
          <div className="px-4 py-3 border-b border-[#262626]">
            <div className="relative">
              <button
                onClick={() => setMotoSelectorOpen(!motoSelectorOpen)}
                className="w-full flex items-center gap-3 p-2 bg-[#121212] border border-[#262626] hover:border-[#FF5F1F]/50 transition-colors"
                data-testid="moto-selector-btn"
              >
                {getMotoImageUrl(selectedMoto) ? (
                  <img 
                    src={getMotoImageUrl(selectedMoto)} 
                    alt="" 
                    className="w-10 h-10 object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#FF5F1F]/10 flex items-center justify-center">
                    <Bike className="w-5 h-5 text-[#FF5F1F]" />
                  </div>
                )}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {selectedMoto ? `${selectedMoto.marca} ${selectedMoto.modello}` : t('motoSelector.selectMoto')}
                  </p>
                  <p className="text-[#9CA3AF] text-xs font-mono">
                    {selectedMoto?.targa || '—'}
                  </p>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#9CA3AF] transition-transform ${motoSelectorOpen ? 'rotate-180' : ''}`} />
              </button>

              {motoSelectorOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0a0a0a] border border-[#262626] z-50 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => { selectMoto(null); setMotoSelectorOpen(false); }}
                    className={`w-full flex items-center gap-3 p-3 hover:bg-[#FF5F1F]/10 transition-colors ${!selectedMotoId ? 'bg-[#FF5F1F]/10' : ''}`}
                    data-testid="moto-option-all"
                  >
                    <div className="w-8 h-8 bg-[#262626] flex items-center justify-center">
                      <Bike className="w-4 h-4 text-[#9CA3AF]" />
                    </div>
                    <span className="text-[#9CA3AF] text-sm">{t('motoSelector.allMoto')}</span>
                  </button>
                  {motoList.map((moto) => (
                    <button
                      key={moto.id}
                      onClick={() => { selectMoto(moto.id); setMotoSelectorOpen(false); }}
                      className={`w-full flex items-center gap-3 p-3 hover:bg-[#FF5F1F]/10 transition-colors ${selectedMotoId === moto.id ? 'bg-[#FF5F1F]/10' : ''}`}
                      data-testid={`moto-option-${moto.id}`}
                    >
                      {getMotoImageUrl(moto) ? (
                        <img 
                          src={getMotoImageUrl(moto)} 
                          alt="" 
                          className="w-8 h-8 object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-[#FF5F1F]/10 flex items-center justify-center">
                          <Bike className="w-4 h-4 text-[#FF5F1F]" />
                        </div>
                      )}
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-white text-sm truncate">{moto.marca} {moto.modello}</p>
                        <p className="text-[#9CA3AF] text-xs font-mono">{moto.targa}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        <nav className="sidebar-nav">
          {navItems.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `sidebar-link list-item ${isActive ? 'active' : ''}`
              }
              style={{ animationDelay: `${index * 0.02}s` }}
              data-testid={`nav-${item.to.slice(1)}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        
        <div className="p-4 border-t border-[#262626]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-white">{user?.nome}</p>
              <p className="text-xs text-[#9CA3AF]">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 text-[#9CA3AF] hover:text-[#FF5F1F] text-sm uppercase tracking-wider transition-colors duration-150"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('auth.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header - with iOS safe area support */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a] border-b border-[#262626] px-4 py-3 mobile-header-safe" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="relative w-8 h-8 rounded overflow-hidden">
                <img 
                  src={LOGO_URL} 
                  alt="Vault Ride" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-['Teko'] text-xl text-[#FF5F1F] uppercase">Vault Ride</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay - with iOS safe area support */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-[#050505] flex flex-col mobile-menu-safe" style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px))' }}>
          {/* Mobile Moto Selector */}
          {motoList.length > 0 && (
            <div className="px-4 py-3 border-b border-[#262626] flex-shrink-0">
              <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-2">{t('motoSelector.selectedMoto')}</p>
              <select
                value={selectedMotoId || ''}
                onChange={(e) => selectMoto(e.target.value || null)}
                className="w-full bg-[#121212] border border-[#262626] text-white p-3 text-sm"
                data-testid="mobile-moto-selector"
              >
                <option value="">{t('motoSelector.allMoto')}</option>
                {motoList.map((moto) => (
                  <option key={moto.id} value={moto.id}>
                    {moto.marca} {moto.modello} ({moto.targa})
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Scrollable Nav */}
          <nav className="p-4 flex-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-4 text-base ${
                    isActive ? 'text-[#FF5F1F] bg-[#FF5F1F]/10' : 'text-[#9CA3AF]'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          {/* Logout Button - Fixed at bottom */}
          <div className="p-4 border-t border-[#262626] flex-shrink-0 bg-[#050505]">
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 px-4 py-4 text-base text-[#FF5F1F] hover:text-white w-full bg-[#FF5F1F]/10 hover:bg-[#FF5F1F]/20 transition-colors"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-semibold uppercase tracking-wider">{t('auth.logout')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {/* Language & Avatar in top right corner - Desktop only */}
        <div className="hidden md:flex fixed top-4 right-4 z-30 items-center gap-2">
          <LanguageSelector />
          <button
            onClick={() => setAvatarDialogOpen(true)}
            className="relative group"
            data-testid="avatar-btn"
          >
            {getAvatarUrl() ? (
              <img 
                src={getAvatarUrl()} 
                alt={user?.nome} 
                className="w-10 h-10 rounded-full object-cover border-2 border-[#262626] group-hover:border-[#FF5F1F] transition-colors"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#FF5F1F]/20 border-2 border-[#262626] group-hover:border-[#FF5F1F] transition-colors flex items-center justify-center">
                <User className="w-5 h-5 text-[#FF5F1F]" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#FF5F1F] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-2.5 h-2.5 text-white" />
            </div>
          </button>
        </div>
        <SubscriptionGuard>
          {children}
        </SubscriptionGuard>
      </main>

      {/* Avatar Dialog */}
      <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
              {t('profile.yourAvatar')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {getAvatarUrl() ? (
              <img 
                src={getAvatarUrl()} 
                alt={user?.nome} 
                className="w-32 h-32 rounded-full object-cover border-4 border-[#262626]"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-[#FF5F1F]/20 border-4 border-[#262626] flex items-center justify-center">
                <User className="w-16 h-16 text-[#FF5F1F]" />
              </div>
            )}
            <p className="text-white text-lg">{user?.nome}</p>
            <p className="text-[#9CA3AF] text-sm">{user?.email}</p>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarUpload}
              accept="image/*"
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold"
            >
              {uploadingAvatar ? t('profile.uploading') : t('profile.changeAvatar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tour Guide for new users */}
      <TourGuide 
        open={showTour} 
        onClose={() => setShowTour(false)} 
        onComplete={handleCompleteTour}
      />

      {/* Floating Support Button */}
      <SupportButton />
    </div>
  );
};
